import { memo, PropsWithChildren, ReactNode } from 'react';
import { Button } from 'src/modules/common/components/Button/Button';
import { FilterIcon } from 'src/modules/common/icons/FilterIcon';
import { Pagination } from 'src/modules/common/components/Pagination/Pagination';
import { CircleFilledIcon } from 'src/modules/common/icons/CircleFilledIcon';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu/OverflowMenu.tsx';
import { SortIcon } from 'src/modules/common/icons/SortIcon.tsx';
import { OverflowMenuItem } from 'src/modules/common/components/OverflowMenu/OverflowMenuItem.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { ProductAttributeId, QueryParamName } from 'src/utils/composeFilter.ts';
import { DirectionEnum, OrderBy } from 'src/generated/operations.ts';
import { ArrowDownIcon } from 'src/modules/common/icons/ArrowDownIcon.tsx';
import { ArrowUpIcon } from 'src/modules/common/icons/ArrowUpIcon.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';

export type OrderItem = [QueryParamName, ProductAttributeId, DirectionEnum];
export type OrderItems = OrderItem[];

type Props = PropsWithChildren<{
    isMobile: boolean;
    currentPage: number;
    pageSize: number;
    hasAdditionalFilter: boolean;
    total: number;
    filterHandler?: () => void;
    onPageChange: (page: number) => void;
    onOrderByChange: (order: OrderBy[]) => void;
    orderItems: OrderItems;
    orderSelected: OrderBy[];
    defaultOrderLabel: string;
    additionalActions?: ReactNode;
}>

const getDirection = (condition: boolean, defaultDirection: DirectionEnum): DirectionEnum => {
    const down = defaultDirection === DirectionEnum.Asc ? DirectionEnum.Asc : DirectionEnum.Desc;
    const up = defaultDirection === DirectionEnum.Asc ? DirectionEnum.Desc : DirectionEnum.Asc;
    return condition ? down : up;
};

export const DeviceGrid = memo(({
    isMobile,
    hasAdditionalFilter,
    currentPage,
    pageSize,
    total,
    filterHandler,
    onPageChange,
    onOrderByChange,
    orderItems = [],
    orderSelected,
    defaultOrderLabel,
    additionalActions,
    children,
}: Props) => {
    const shownItemsFrom = ((currentPage - 1) * pageSize) + 1;
    const shownItemsTo = currentPage * pageSize > total ? total : currentPage * pageSize;
    return (
        <div className="vss-device-grid">
            <div className="vss-device-grid__header">
                <p className="vss-device-grid__counter">{shownItemsFrom}-{shownItemsTo} ({total})</p>

                <div className="vss-device-grid__actions">
                    {orderItems.length > 0 && (
                        <OverflowMenu
                            buttonSize="md"
                            kind="ghost"
                            icon={(
                                <>
                                    <SortIcon/>

                                    {orderSelected.length > 0 && (
                                        <div className="vss-device-grid__indicator">
                                            <CircleFilledIcon/>
                                        </div>
                                    )}
                                </>
                            )}
                        >
                            <OverflowMenuItem
                                selected={orderSelected.length === 0}
                                onClick={() => onOrderByChange([])}
                            >
                                <I18n messageId={defaultOrderLabel}/>

                                {orderSelected.length === 0 && (
                                    <div className="vss-device-grid__action-icon">
                                        <ArrowDownIcon/>
                                    </div>
                                )}
                            </OverflowMenuItem>
                            {orderItems.map(([label, id, defaultDirection]) => {
                                const selected = orderSelected.length > 0 &&
                                    orderSelected.filter((item) => ![id].includes(item.id)).length === 0;
                                let direction = defaultDirection;
                                if (orderSelected.length > 0 && selected) {
                                    direction = orderSelected[0].direction === DirectionEnum.Asc
                                        ? DirectionEnum.Desc : DirectionEnum.Asc;
                                }
                                return (
                                    <OverflowMenuItem
                                        key={label}
                                        selected={selected}
                                        onClick={() => onOrderByChange(
                                            [id].map((attrId) => ({ id: attrId, direction })),
                                        )}
                                    >
                                        <I18n messageId={label}/>
                                        {selected && (
                                            <div className="vss-device-grid__action-icon">
                                                {(direction === getDirection(true, defaultDirection)) ? (
                                                    <ArrowUpIcon/>
                                                ) : (
                                                    <ArrowDownIcon/>
                                                )}
                                            </div>
                                        )}
                                    </OverflowMenuItem>
                                );
                            })}
                        </OverflowMenu>
                    )}

                    {additionalActions}

                    {filterHandler && (
                        <Button
                            kind="tertiary"
                            size="lg"
                            onClick={filterHandler}
                        >
                            <I18n messageId={MESSAGES.deviceGridActionFilter}/>

                            <div className="vss-device-grid__action-icon">
                                <FilterIcon/>
                            </div>

                            {hasAdditionalFilter && (
                                <div className="vss-device-grid__indicator">
                                    <CircleFilledIcon/>
                                </div>
                            )}
                        </Button>
                    )}
                </div>
            </div>

            <div className="vss-device-grid__items">
                {children}
            </div>

            {total > pageSize && (
                <Pagination
                    isMobile={isMobile}
                    currentPage={currentPage}
                    total={total}
                    pageSize={pageSize}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
});
